import { useEffect, useRef } from "react";
import { MapPin } from "lucide-react";

/**
 * Reusable Google Places address input.
 * The Google Maps JS + places library is loaded globally in public/index.html.
 *
 * Props:
 *  - value: string (controlled text value)
 *  - onChange(text): fired on every keystroke
 *  - onSelect(parts): fired when a place is picked. `parts` = {
 *      formatted, street, city, state_province, postal_code, country
 *    }
 *  - placeholder, className, id, name, required, disabled
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address",
  className = "",
  id,
  name,
  required = false,
  disabled = false,
  icon = true,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    let listener = null;

    // The Maps script loads async — it may not be ready when this mounts. Try now,
    // then again when the loader fires pawlinx:maps-ready (see public/index.html).
    // If the key is missing or auth fails, this never initializes and the field
    // remains a normal text input.
    const init = () => {
      if (listener || window.__PAWLINX_MAPS_FAILED) return true;
      if (!window.google?.maps?.places) return false;
      const ac = new window.google.maps.places.Autocomplete(input, {
        types: ["address"],
        fields: ["address_components", "formatted_address"],
      });
      listener = ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place.address_components) return;
        const get = (t) => place.address_components.find((c) => c.types.includes(t))?.long_name ?? "";
        const gets = (t) => place.address_components.find((c) => c.types.includes(t))?.short_name ?? "";
        const parts = {
          formatted: place.formatted_address || "",
          street: [get("street_number"), get("route")].filter(Boolean).join(" "),
          city: get("locality") || get("sublocality") || get("administrative_area_level_2"),
          state_province: gets("administrative_area_level_1"),
          postal_code: get("postal_code"),
          country: gets("country"),
        };
        onSelect?.(parts);
      });
      return true;
    };

    const onReady = () => init();
    if (!init()) window.addEventListener("pawlinx:maps-ready", onReady);
    return () => {
      window.removeEventListener("pawlinx:maps-ready", onReady);
      if (listener) window.google?.maps?.event?.removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative">
      {icon && (
        <MapPin className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      )}
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        className={className || `w-full ${icon ? "pl-10" : "pl-4"} pr-4 py-3 rounded-xl border border-paw-border bg-white text-paw-text placeholder:text-paw-text-muted focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary transition`}
      />
    </div>
  );
}
